// @core/stores/affiliate/AffiliateStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  Affiliate,
  AffiliateResponse,
  ApproveAffiliateRequest,
  ChangeAffiliateTierRequest,
  CreateAffiliateRequest,
  GetAffiliateStatsResponse,
  SuspendAffiliateRequest,
  UpdateAffiliateRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateStore {
  // List state
  items: Affiliate[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Affiliate | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Affiliate>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Affiliate | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Affiliate | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Approve affiliate application
   */
  async ApproveAffiliate(affiliate_id: string, data: ApproveAffiliateRequest): Promise<AffiliateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ApproveAffiliate(affiliate_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Change affiliate tier
   */
  async ChangeAffiliateTier(affiliate_id: string, data: ChangeAffiliateTierRequest): Promise<AffiliateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ChangeAffiliateTier(affiliate_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register as affiliate
   */
  async CreateAffiliate(data: CreateAffiliateRequest): Promise<Affiliate> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreateAffiliate(data);
      const item = response.affiliate;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get affiliate by ID
   */
  async GetAffiliate(affiliate_id: string): Promise<Affiliate> {
    // Check cache first
    const cached = this.cache.get(affiliate_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetAffiliate(affiliate_id);
      const item = response.affiliate;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get affiliate statistics
   */
  async GetAffiliateStats(affiliate_id: string, params?: { period_end?: string | number | boolean; period_start?: string | number | boolean; }): Promise<GetAffiliateStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.GetAffiliateStats(affiliate_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List affiliates
   */
  async ListAffiliates(params?: PaginatedRequest & { q?: string | number | boolean; status?: string | number | boolean; tier_id?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListAffiliates(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Suspend affiliate
   */
  async SuspendAffiliate(affiliate_id: string, data: SuspendAffiliateRequest): Promise<AffiliateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.SuspendAffiliate(affiliate_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update affiliate profile
   */
  async UpdateAffiliate(affiliate_id: string, data: UpdateAffiliateRequest): Promise<Affiliate> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.UpdateAffiliate(affiliate_id, data);
      const item = response.affiliate;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const affiliateStore = new AffiliateStore();
